/* oxlint-disable typescript/no-explicit-any -- schema library requires flexible any types */
/* oxlint-disable typescript/no-unsafe-assignment, typescript/no-unsafe-call, typescript/no-unsafe-member-access, typescript/no-unsafe-return -- schema library uses any types throughout */
import { BaseSchema } from '../core/base-schema'
export type {
  OptionalSchema,
  OptionalSchemaDef,
  OptionalSchemaInternals,
} from '../core/base-schema'
export type {
  NullableSchema,
  NullableSchemaDef,
  NullableSchemaInternals,
} from '../core/base-schema'
export { optional, nullable } from '../core/base-schema'
import type { OptionalSchema, NullableSchema } from '../core/base-schema'
import * as errors from '../core/errors'
import * as types from '../core/types'

// Refine wrapper - adds custom validation
export interface RefineSchemaDef<T extends types.Schema>
  extends types.SchemaTypeDef {
  type: 'refine'
  schema: T
  refinement: (data: types.output<T>) => boolean | Promise<boolean>
  message?: string | (() => string)
}

export interface RefineSchema<T extends types.Schema> extends types.Schema<
  types.output<T>,
  types.input<T>
> {
  meta(metadata: Record<string, any>): this
  optional(): OptionalSchema<this>
  nullable(): NullableSchema<this>
  parse(data: unknown, params?: types.ParseContext): types.output<T>
  safeParse(
    data: unknown,
    params?: types.ParseContext,
  ):
    | { success: true; data: types.output<T> }
    | { success: false; error: errors.SchemaError }
}

export const RefineSchema: types.$constructor<
  RefineSchema<any>,
  RefineSchemaDef<any>
> = types.$constructor(
  'RefineSchema',
  (inst: RefineSchema<any>, def: RefineSchemaDef<any>) => {
    BaseSchema.init(inst, def)

    inst._zod.optin = def.schema._zod.optin
    inst._zod.optout = def.schema._zod.optout

    inst._zod.parse = (payload, ctx) => {
      // First parse with underlying schema
      const result: types.ParsePayload | Promise<types.ParsePayload> = def.schema._zod.run(payload, ctx)

      if (result instanceof Promise) {
        return result.then(async (r) => {
          if (r.issues.length > 0) return r

          const isValid = await def.refinement(r.value)
          if (!isValid) {
            const msg = def.message
              ? typeof def.message === 'function'
                ? def.message()
                : def.message
              : undefined
            r.issues.push({
              code: 'custom',
              continue: true,
              input: r.value,
              inst,
              ...(msg ? { message: msg } : {}),
            })
          }
          return r
        })
      }

      if (result.issues.length > 0) return result

      const isValid = def.refinement(result.value)
      if (isValid instanceof Promise) {
        if (ctx.async === false) throw new types.AsyncError()
        return isValid.then((valid) => {
          if (!valid) {
            const msg = def.message
              ? typeof def.message === 'function'
                ? def.message()
                : def.message
              : undefined
            result.issues.push({
              code: 'custom',
              continue: true,
              input: result.value,
              inst,
              ...(msg ? { message: msg } : {}),
            })
          }
          return result
        })
      }

      if (!isValid) {
        const msg = def.message
          ? typeof def.message === 'function'
            ? def.message()
            : def.message
          : undefined
        result.issues.push({
          code: 'custom',
              continue: true,
          input: result.value,
          inst,
          ...(msg ? { message: msg } : {}),
        })
      }

      return result
    }
  },
)

export function refine<T extends types.Schema>(
  schema: T,
  refinement: (data: types.output<T>) => boolean | Promise<boolean>,
  message?: string | { message: string | (() => string) },
): RefineSchema<T> {
  return new RefineSchema({
    type: 'refine',
    schema,
    refinement,
    message: typeof message === 'string' ? message : message?.message,
  })
}

// Transform wrapper - transforms output
export interface TransformSchemaDef<T extends types.Schema, Out>
  extends types.SchemaTypeDef {
  type: 'transform'
  schema: T
  transform: (data: types.output<T>) => Out | Promise<Out>
}

export interface TransformSchema<
  T extends types.Schema,
  Out,
> extends types.Schema<Out, types.input<T>> {
  meta(metadata: Record<string, any>): this
  optional(): OptionalSchema<this>
  nullable(): NullableSchema<this>
  parse(data: unknown, params?: types.ParseContext): Out
  safeParse(
    data: unknown,
    params?: types.ParseContext,
  ):
    | { success: true; data: Out }
    | { success: false; error: errors.SchemaError }
}

export const TransformSchema: types.$constructor<
  TransformSchema<any, any>,
  TransformSchemaDef<any, any>
> = types.$constructor(
  'TransformSchema',
  (inst: TransformSchema<any, any>, def: TransformSchemaDef<any, any>) => {
    BaseSchema.init(inst, def)

    inst._zod.optin = def.schema._zod.optin

    inst._zod.parse = (payload, ctx) => {
      // First parse with underlying schema
      const result: types.ParsePayload | Promise<types.ParsePayload> = def.schema._zod.run(payload, ctx)

      if (result instanceof Promise) {
        return result.then(async (r) => {
          if (r.issues.length > 0) return r
          r.value = await def.transform(r.value)
          return r
        })
      }

      if (result.issues.length > 0) return result

      const transformed = def.transform(result.value)
      if (transformed instanceof Promise) {
        if (ctx.async === false) throw new types.AsyncError()
        return transformed.then((t) => {
          result.value = t
          return result
        })
      }

      result.value = transformed
      return result
    }
  },
)

export function transform<T extends types.Schema, Out>(
  schema: T,
  transformer: (data: types.output<T>) => Out | Promise<Out>,
): TransformSchema<T, Out> {
  return new TransformSchema({
    type: 'transform',
    schema,
    transform: transformer,
  })
}

// Default wrapper - provides default value
export interface DefaultSchemaDef<T extends types.Schema>
  extends types.SchemaTypeDef {
  type: 'default'
  schema: T
  defaultValue: types.output<T> | (() => types.output<T>)
}

export interface DefaultSchema<T extends types.Schema> extends types.Schema<
  types.output<T>,
  types.input<T> | undefined,
  types.SchemaInternals<types.output<T>, types.input<T> | undefined> & { optin: 'defaulted' }
> {
  meta(metadata: Record<string, any>): this
  optional(): OptionalSchema<this>
  nullable(): NullableSchema<this>
  parse(data: unknown, params?: types.ParseContext): types.output<T>
  safeParse(
    data: unknown,
    params?: types.ParseContext,
  ):
    | { success: true; data: types.output<T> }
    | { success: false; error: errors.SchemaError }
}

export const DefaultSchema: types.$constructor<
  DefaultSchema<any>,
  DefaultSchemaDef<any>
> = types.$constructor(
  'DefaultSchema',
  (inst: DefaultSchema<any>, def: DefaultSchemaDef<any>) => {
    BaseSchema.init(inst, def)

    inst._zod.optin = 'defaulted'

    inst._zod.parse = (payload, ctx) => {
      // If undefined, use default
      if (payload.value === undefined) {
        payload.value =
          typeof def.defaultValue === 'function'
            ? def.defaultValue()
            : cloneDefault(def.defaultValue)
        return payload
      }

      const result: types.ParsePayload | Promise<types.ParsePayload> = def.schema._zod.run(payload, ctx)
      const finish = (p: types.ParsePayload) => {
        if (p.value === undefined) p.value = typeof def.defaultValue === 'function' ? def.defaultValue() : cloneDefault(def.defaultValue)
        return p
      }
      if (result instanceof Promise) return result.then(finish)
      return finish(result)
    }
  },
)

export function _default<T extends types.Schema>(
  schema: T,
  defaultValue: types.output<T> | (() => types.output<T>),
): DefaultSchema<T> {
  return new DefaultSchema({
    type: 'default',
    schema,
    defaultValue,
  })
}

// Catch wrapper - catches errors and returns fallback
export interface CatchContext extends types.ParsePayload {
  error: { issues: errors.Issue[] }
  input: unknown
}

export interface CatchSchemaDef<T extends types.Schema>
  extends types.SchemaTypeDef {
  type: 'catch'
  schema: T
  catchValue: types.output<T> | ((ctx: CatchContext) => types.output<T>)
}

export interface CatchSchema<T extends types.Schema> extends types.Schema<
  types.output<T>,
  types.input<T>
> {
  meta(metadata: Record<string, any>): this
  optional(): OptionalSchema<this>
  nullable(): NullableSchema<this>
  parse(data: unknown, params?: types.ParseContext): types.output<T>
  safeParse(
    data: unknown,
    params?: types.ParseContext,
  ):
    | { success: true; data: types.output<T> }
    | { success: false; error: errors.SchemaError }
}

export const CatchSchema: types.$constructor<
  CatchSchema<any>,
  CatchSchemaDef<any>
> = types.$constructor(
  'CatchSchema',
  (inst: CatchSchema<any>, def: CatchSchemaDef<any>) => {
    BaseSchema.init(inst, def)

    inst._zod.optin = def.schema._zod.optin === 'defaulted' ? 'defaulted' : 'optional'
    inst._zod.optout = def.schema._zod.optout
    inst._zod.parse = (payload, ctx) => {
      // Isolate inner issues: catch must not consume issues owned by its caller.
      const result: types.ParsePayload | Promise<types.ParsePayload> = def.schema._zod.run({ value: payload.value, issues: [] }, ctx)
      const finish = (inner: types.ParsePayload) => {
        payload.value = inner.issues.length === 0 ? inner.value : typeof def.catchValue === 'function' ? def.catchValue({ ...inner, value: payload.value, input: payload.value, error: { issues: inner.issues.map(issue => errors.formatIssue(issue, ctx.error)) } }) : def.catchValue
        return payload
      }
      if (result instanceof Promise) return result.then(finish)
      return finish(result)
    }
  },
)

export function _catch<T extends types.Schema>(
  schema: T,
  catchValue: types.output<T> | ((ctx: CatchContext) => types.output<T>),
): CatchSchema<T> {
  return new CatchSchema({
    type: 'catch',
    schema,
    catchValue,
  })
}

// Preprocess wrapper - transforms input before validation
export interface PreprocessSchemaDef<T extends types.Schema>
  extends types.SchemaTypeDef {
  type: 'preprocess'
  schema: T
  preprocess: (data: unknown) => types.input<T>
}

export interface PreprocessSchema<T extends types.Schema> extends types.Schema<
  types.output<T>
> {
  meta(metadata: Record<string, any>): this
  optional(): OptionalSchema<this>
  nullable(): NullableSchema<this>
  parse(data: unknown, params?: types.ParseContext): types.output<T>
  safeParse(
    data: unknown,
    params?: types.ParseContext,
  ):
    | { success: true; data: types.output<T> }
    | { success: false; error: errors.SchemaError }
}

export const PreprocessSchema: types.$constructor<
  PreprocessSchema<any>,
  PreprocessSchemaDef<any>
> = types.$constructor(
  'PreprocessSchema',
  (inst: PreprocessSchema<any>, def: PreprocessSchemaDef<any>) => {
    BaseSchema.init(inst, def)

    inst._zod.optin = 'optional'
    inst._zod.optout = def.schema._zod.optout

    inst._zod.parse = (payload, ctx) => {
      payload.value = def.preprocess(payload.value)
      return def.schema._zod.run(payload, ctx)
    }
  },
)

export function preprocess<T extends types.Schema>(
  fn: (data: unknown) => types.input<T>,
  schema: T,
): PreprocessSchema<T> {
  return new PreprocessSchema({
    type: 'preprocess',
    schema,
    preprocess: fn,
  })
}

function cloneDefault<T>(value: T): T {
  if (Array.isArray(value)) return [...value] as T
  if (value instanceof Map) return new Map(value) as T
  if (value instanceof Set) return new Set(value) as T
  if (value && typeof value === 'object' && (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null)) return { ...value }
  return value
}
